import cn from 'classnames';
import { useContext } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';

import type { MultiSelectProps, SelectOption, SelectProps } from './lib';

export const getStyles = ({ select, text, view, maxWidth }): Record<string, any> => {
    const isTransparent = view === 'transparent';

    return {
        menu: (base: CSSProperties) => ({
            ...base,
            ...(maxWidth ? { width: 'max-content', minWidth: '100%' } : {}),
            zIndex: 700000,
        }),
        menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 700000 }),
        control: (base: CSSProperties) => {
            const result = {
                ...base,
                minHeight: '34px',
                backgroundColor: select.root?.backgroundColor || 'var(--c-border)',
                borderColor: select.root?.borderColor || 'var(--c-border)',
                borderRadius: '8px',
                boxShadow: 'none',
                ':hover': select.root?.borderColor
                    ? {
                          borderColor: `${select.root?.['--c-border-focus']} !important`,
                      }
                    : {},
            };

            if (isTransparent) {
                result.backgroundColor = 'transparent';
                result.border = 'none';
            }

            return result;
        },
        valueContainer: (base: CSSProperties) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: '2px',
        }),
        container: (base: CSSProperties) => ({ ...base, ...text }),
        dropdownIndicator: (base: CSSProperties) => ({
            ...base,
            padding: isTransparent ? '0' : '6px',
            color: select.arrow?.color ? `${select.arrow?.color} !important` : base.color,
            ':hover': {
                color: base.color,
            },
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        singleValue: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
        }),
        multiValueLabel: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
        }),
        multiValue: (base: CSSProperties) => ({
            ...base,
            borderRadius: '6px',
        }),
        option: (base: CSSProperties) => ({
            ...base,
            wordBreak: 'break-word',
        }),
        multiValueRemove: (base: CSSProperties) => ({
            ...base,
            cursor: 'pointer',
        }),
        clearIndicator: (base: CSSProperties) => ({
            ...base,
            paddingTop: '6px',
            paddingBottom: '6px',
            cursor: 'pointer',
        }),
    };
};

export const useGetCommonProps = ({
    id,
    options,
    isOpen,
    disabled,
    placeholder = 'Выбрать из списка',
    className,
    classNames,
    searchable = false,
    clearable,
    menuPosition,
    view,
    stretchMenuToLongestOption,
}: SelectProps | MultiSelectProps): Record<string, any> => {
    const { select, text } = useContext(CustomStylesCtx);
    const styles = getStyles({ select, text, view, maxWidth: stretchMenuToLongestOption });

    return {
        id,
        options,
        styles,
        placeholder,
        menuPosition: menuPosition || 'fixed',
        noOptionsMessage: () => '',
        defaultMenuIsOpen: isOpen,
        isDisabled: disabled,
        className: cn(CssUiComponent.SELECT, className),
        classNames: {
            control: () => classNames?.control,
            option: () => classNames?.option,
            placeholder: () => classNames?.placeholder,
        },
        isSearchable: searchable,
        isClearable: clearable,
        menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
        formatOptionLabel: (option: SelectOption) => {
            return option.isHtml ? <div {...setHtml(option.label)} /> : option.label;
        },
        filterOption: (option: SelectOption, inputValue: string) => {
            if (typeof option.label === 'string') {
                return option.label.toLowerCase().includes(inputValue.toLowerCase());
            }

            return true;
        },
    };
};
